import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@/app/providers/AuthProvider';
import { supabase } from '@/lib/supabase';

function consentKeys(siteKey?: string) {
  const hostnameKey = `lumenise-cms-consent:${window.location.hostname.toLowerCase()}`;
  const normalizedSiteKey = siteKey?.trim();
  return normalizedSiteKey
    ? [`lumenise-cms-consent:site:${normalizedSiteKey}`, hostnameKey]
    : [hostnameKey];
}

function hasSavedConsent(siteKey?: string) {
  try {
    return consentKeys(siteKey).some((key) => localStorage.getItem(key) === 'accepted' || localStorage.getItem(key) === 'declined');
  } catch {
    return false;
  }
}

function saveConsent(value: 'accepted' | 'declined', siteKey?: string) {
  try {
    // Mantém a chave antiga para não reexibir o banner a visitantes que já
    // consentiram antes da adoção do identificador estável do site.
    consentKeys(siteKey).forEach((key) => localStorage.setItem(key, value));
  } catch {
    // Alguns navegadores bloqueiam storage; o consentimento ainda vale para
    // a sessão atual porque o componente é ocultado após a escolha.
  }
}

function privacyHref() {
  const match = window.location.pathname.match(/^\/s\/([^/]+)/);
  return match ? `/s/${match[1]}/legal/privacidade` : '/legal/privacidade';
}

export function CookieBanner({ siteKey }: { siteKey?: string }) {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(!hasSavedConsent(siteKey));
  }, [siteKey]);

  const recordConsent = useMutation({
    mutationFn: async (granted: boolean) => {
      if (!user) return;
      await supabase.from('user_consents').insert({
        user_id: user.id,
        consent_type: 'analytics',
        consent_version: 'v1.0.0',
        granted,
        user_agent: navigator.userAgent,
      });
    },
  });

  const accept = () => {
    saveConsent('accepted', siteKey);
    window.dispatchEvent(new Event('lumenise:analytics-consent'));
    recordConsent.mutate(true);
    setVisible(false);
  };

  const decline = () => {
    saveConsent('declined', siteKey);
    window.dispatchEvent(new Event('lumenise:analytics-consent'));
    recordConsent.mutate(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border p-4 shadow-lg">
      <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-sm text-muted">
          Usamos cookies essenciais e opcionais para analytics. Veja nossa{' '}
          <a href={privacyHref()} className="text-accent hover:underline">Política de Privacidade</a>.
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={decline}
            className="px-4 py-2 rounded-lg border border-border text-sm text-muted hover:bg-surface"
          >
            Apenas essenciais
          </button>
          <button
            onClick={accept}
            className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90"
          >
            Aceitar
          </button>
        </div>
      </div>
    </div>
  );
}
